import React, { useReducer, useState, useCallback, useEffect } from "react";
//import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import { HashRouter as Router, Routes, Route, Link } from "react-router-dom";
import { Transmission } from "./components/Transmission";
import NotePage from "./pages/Note";
import DataPage from "./pages/Data";
import AboutPage from "./pages/About";
import PathRenderer from "./components/PathRenderer";
import { classifyData } from "./DataService"; 
import "./assets/styles/App.css";

const defaultState = {
  num1: "", //当前数量
  num2: "", //目标数量
  result: "", //两者相差
  error1: "", // num1错误信息
  error2: "", // num2错误信息
  history: [], //计算历史记录
  differenceError: "", //差值错误信息
  pathCache: [], //计算出来的路径缓存
  currentPathIndex: 0, //路径索引
  clickCount: 0, // 路径切换次数
  isCalculating: false,
  settings: {
    disable3Star: false,
    disable2Star: false,
    disableMaterial: false,
    disableStore20: false,
    disableStore10: false,
    disableStore70: false,
    disableStore2000: false,
    disableStore5000: false,
    disableCE: false,
    disableExt25: false,
    disableTrade: false,
    enableUpgradeOnly0: false,
    enableUpgradeOnly1: false,
    enableUpgradeOnly2: false,
    enableUpgradeOnlyFor1: false,
  },
  upgrade0Count: "",
  upgrade1Count: "",
  upgrade2Count: "",
  sanityCount: "",
};

// [新增] 在组件外部定义好你的图片 URL 数组
const romanticImageUrls = [
  "https://ark-lmd.oss-cn-beijing.aliyuncs.com/rosmontis1.webp",
  "https://ark-lmd.oss-cn-beijing.aliyuncs.com/rosmontis2.webp",
  "https://ark-lmd.oss-cn-beijing.aliyuncs.com/rosmontis3.webp",
  "https://ark-lmd.oss-cn-beijing.aliyuncs.com/rosmontis4.webp",
];

const funnyImageUrl = "https://ark-lmd.oss-cn-beijing.aliyuncs.com/114514.webp"; 

// 默认设置按钮的初始状态
const getInitialState = () => {
  const savedState = localStorage.getItem("calculatorState");
  const initialState = savedState ? JSON.parse(savedState) : defaultState;
  initialState.settings.disableStore10 = true;
  initialState.settings.disableStore20 = true;
  initialState.settings.disableStore70 = true;
  initialState.settings.disableStore2000 = true;
  initialState.settings.disableStore5000 = true;
  initialState.settings.disableExt25 = true;
  initialState.settings.enableUpgradeOnly0 = true;
  initialState.settings.enableUpgradeOnly1 = true;
  initialState.settings.enableUpgradeOnly2 = true;
  initialState.settings.enableUpgradeOnlyFor1 = true;
  return initialState;
};

const reducer = (state, action) => {
  switch (action.type) {
    case "SET_NUM":
      return {
        ...state,
        [action.field]: action.value,
        [`error${action.field.slice(-1)}`]: "",
      };
    case "SET_ERROR":
      return {
        ...state,
        [action.field]: action.value,
      };
    case "SET_RESULT":
      return {
        ...state,
        result: action.value,
      };
    case "SET_PATHS":
      console.log("Reducer SET_PATHS:", action.paths);
      return {
        ...state,
        pathCache: action.paths,
        currentPathIndex: 0,
        clickCount: 0,
      };
    case "SET_HISTORY":
      return {
        ...state,
        history: action.history,
      };
    case "SET_CALCULATING":
      return {
        ...state,
        isCalculating: action.value,
      };
    case "TOGGLE_SETTING":
      return {
        ...state,
        settings: {
          ...state.settings,
          [action.key]: !state.settings[action.key],
        },
      };
    case "CHANGE_PATH":
      return {
        ...state,
        currentPathIndex:
          (state.currentPathIndex + action.delta + state.pathCache.length) %
          state.pathCache.length,
        clickCount: state.clickCount + 1,
      };
    case "SET_UPGRADE_COUNT":
      return {
        ...state,
        [action.field]: action.value,
      };
    default:
      return state;
  }
};

// [新增] 在文件顶部或 MainCalculator 组件外部添加一个辅助函数
// 辅助函数：检查一个数是否是10的整数次幂 (1, 10, 100, ...)
const isPowerOfTen = (n) => {
  if (n <= 0) return false; // 10的幂不可能是0或负数
  // 通过计算以10为底的对数，如果结果是整数，则是10的幂
  // 使用一个小的误差容忍(epsilon)来处理浮点数精度问题
  const log = Math.log10(n);
  return Math.abs(log - Math.round(log)) < 1e-10;
};

const isRomanticNumber = (numStr) => {
  // 基础验证
  if (!numStr || typeof numStr !== 'string') {
    return false;
  }

  // 检查纯粹组合
  const pureComboRegex = /^(520|1314)+$/;
  if (pureComboRegex.test(numStr)) {
    return true;
  }

  // 检查是否为幂倍数
  const num = parseInt(numStr, 10);
  // 如果转换失败或数字为0，则不是
  if (isNaN(num) || num === 0) {
    return false;
  }

  if (num >= 520 && num % 520 === 0) {
    const quotient = num / 520;
    if (isPowerOfTen(quotient)) {
      return true;
    }
  }

  if (num >= 1314 && num % 1314 === 0) {
    const quotient = num / 1314;
    if (isPowerOfTen(quotient)) {
      return true;
    }
  }
  return false;
};

const isFunnyNumber = (numStr) => {
  if (!numStr || typeof numStr !== 'string') return false;
  
  const num = parseInt(numStr, 10);
  if (isNaN(num) || num === 0) return false;

  // 检查是否幂倍数
  if (num >= 114514 && num % 114514 === 0) {
    if (isPowerOfTen(num / 114514)) return true;
  }
  
  if (num >= 1919810 && num % 1919810 === 0) {
    if (isPowerOfTen(num / 1919810)) return true;
  }
  return false;
};

// 主计算组件
const MainCalculator = () => {
  const [state, dispatch] = useReducer(reducer, getInitialState());
  const [showModal, setShowModal] = useState(false); // 弹窗状态
  const [showBonusModal, setShowBonusModal] = useState(false); // 彩蛋弹窗状态
  const [isBonusReady, setIsBonusReady] = useState(false); //彩蛋是否就绪
  const [activeImageUrl, setActiveImageUrl] = useState(""); //存储随机图片的 URL

  useEffect(() => {
    localStorage.setItem("calculatorState", JSON.stringify(state));
  }, [state]);

  //处理弹窗逻辑
  useEffect(() => {
    // 当 "只允许升级" 这个开关被开启时，检查其他相关开关的状态并弹出提示
    if (state.settings.enableUpgradeOnlyFor1) {
      const { enableUpgradeOnly0, enableUpgradeOnly1, enableUpgradeOnly2 } =
        state.settings;
      if (!enableUpgradeOnly0 && !enableUpgradeOnly1 && !enableUpgradeOnly2) {
        // 只有当三个子开关都关闭时才提醒
        setShowModal(true);
      }
    }
  }, [
    state.settings.enableUpgradeOnlyFor1,
    state.settings.enableUpgradeOnly0,
    state.settings.enableUpgradeOnly1,
    state.settings.enableUpgradeOnly2,
  ]); // 依赖项数组

  // 开关变化处理
  /*
  const handleToggleChange = useCallback(
    (key) => {
      dispatch({ type: "TOGGLE_SETTING", key });
      if (
        key === "enableUpgradeOnlyFor1" &&
        !state.settings.enableUpgradeOnlyFor1
      ) {
        setShowModal(true);
      }
    },
    [state.settings]
  );*/

  const handleToggleChange = useCallback(
    (key) => {
      // 就让它只负责“按下开关”，别的啥也不干
      dispatch({ type: "TOGGLE_SETTING", key });
    },
    [] // 这里的依赖项可以清空
  );

  // 优化后的输入验证
  const handleInputChange = useCallback((e, field) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    if (value === "") {
      dispatch({ type: "SET_NUM", field, value: "" });
      return;
    }

    const numValue = parseInt(value, 10);
    if (numValue > 99999999) {
      dispatch({
        type: "SET_ERROR",
        field: `error${field.slice(-1)}`,
        value: "你真的有这么多龙门币吗？",
      });
      return;
    }
    dispatch({ type: "SET_NUM", field, value: numValue.toString() });
  }, []);

  // 处理左边限制类数量输入
  const handleUpgradeCountChange = useCallback((e, field, min, max) => {
    const value = e.target.value;
    if (value === "") {
      dispatch({ type: "SET_UPGRADE_COUNT", field, value: "" });
      return;
    }

    const numValue = parseInt(value, 10);
    if (isNaN(numValue) || numValue < min || numValue > max) {
      return;
    }
    dispatch({ type: "SET_UPGRADE_COUNT", field, value: numValue.toString() });
  }, []);

  // 主体计算逻辑 异步函数
  const handleCalculate = useCallback(
    async (event) => {
      // [新增] 特殊数字彩蛋逻辑
      if (isRomanticNumber(state.num2)) {
        createHeartEffect(event);
        const randomIndex = Math.floor(
          Math.random() * romanticImageUrls.length
        );
        setActiveImageUrl(romanticImageUrls[randomIndex]);
      } else if (isFunnyNumber(state.num2)) {
        // 如果是趣味数字，显示图片5
        setActiveImageUrl(funnyImageUrl);
        // 可选：在这里也可以添加一个不同的点击特效
      } else {
        // 如果都不是，清空图片
        setActiveImageUrl("");
      }

      if (!state.num1 || !state.num2) {
        dispatch({
          type: "SET_ERROR",
          field: "differenceError",
          value: "请检查当前/目标龙门币数量是否填写完整~",
        });
        return;
      }

      // 检查两个数字是否相同
      if (state.num1 === state.num2) {
        dispatch({
          type: "SET_ERROR",
          field: "differenceError",
          value: "好像输入了两个相同的数字，要不检查一下?",
        });
        dispatch({ type: "SET_RESULT", value: "" });
        return;
      }

      const num1Val = parseInt(state.num1, 10);
      const num2Val = parseInt(state.num2, 10);
      const difference = num2Val - num1Val;

      if (Math.abs(difference) > 5000) {
        dispatch({
          type: "SET_ERROR",
          field: "differenceError",
          value: "差值需在-5000~5000之间",
        });
        dispatch({ type: "SET_RESULT", value: "" });
        return;
      }

      dispatch({ type: "SET_ERROR", field: "differenceError", value: "" });
      dispatch({ type: "SET_RESULT", value: difference.toString() });
      dispatch({ type: "SET_CALCULATING", value: true });
      dispatch({ type: "SET_PATHS", paths: [] }); // 开始计算时清空旧路径

      const filteredItems = classifyData.filter((item) => {
        const { settings } = state;
        const itemType = item.type?.toLowerCase() || "";
        const isUpgradeAllowed = settings.enableUpgradeOnlyFor1
          ? itemType !== "upgrade"
          : true;
        return (
          (!settings.disable3Star || itemType !== "3_star") &&
          (!settings.disable2Star || itemType !== "2_star") &&
          (!settings.disableMaterial || itemType !== "material") &&
          (!settings.disableStore20 || itemType !== "store_20") &&
          (!settings.disableStore10 || itemType !== "store_10") &&
          (!settings.disableStore70 || itemType !== "store_70") &&
          (!settings.disableStore2000 || itemType !== "store_2000") &&
          (!settings.disableStore5000 || itemType !== "store_5000") &&
          (!settings.disableCE || itemType !== "ce") &&
          (!settings.disableExt25 || itemType !== "ext_25") &&
          (!settings.disableTrade || itemType !== "trade") &&
          (settings.enableUpgradeOnly0 || itemType !== "upgrade_only_0") &&
          (settings.enableUpgradeOnly1 || itemType !== "upgrade_only_1") &&
          (settings.enableUpgradeOnly2 || itemType !== "upgrade_only_2") &&
          isUpgradeAllowed
        );
      });

      // 提取四个限制值，空值时默认为无限大（不限制）
      const upgrade0Limit =
        state.upgrade0Count === ""
          ? Infinity
          : parseInt(state.upgrade0Count, 10);
      const upgrade1Limit =
        state.upgrade1Count === ""
          ? Infinity
          : parseInt(state.upgrade1Count, 10);
      const upgrade2Limit =
        state.upgrade2Count === ""
          ? Infinity
          : parseInt(state.upgrade2Count, 10);
      const sanityLimit =
        state.sanityCount === "" ? Infinity : parseInt(state.sanityCount, 10);

      //console.log("filteredItems:", filteredItems);

      const cacheKey = `${difference}_${Object.entries(state.settings)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([k, v]) => `${k}:${v}`)
        .join(
          "|"
        )}_${upgrade0Limit}_${upgrade1Limit}_${upgrade2Limit}_${sanityLimit}`;
      //console.log("生成的 cacheKey:", cacheKey);
      const cachedResult = localStorage.getItem(`pathCache_${cacheKey}`);

      if (cachedResult) {
        try {
          const paths = JSON.parse(cachedResult);
          //console.log("从缓存读取路径:", JSON.stringify(paths, null, 2));
          if (!paths || paths.length === 0) {
            //console.log("缓存为空，重新计算");
            localStorage.removeItem(`pathCache_${cacheKey}`);
          } else {
            dispatch({ type: "SET_PATHS", paths });
            dispatch({ type: "SET_CALCULATING", value: false });
            dispatch({
              type: "SET_HISTORY",
              history: [
                ...state.history.slice(-10),
                {
                  path: paths[0],
                  timestamp: new Date().toLocaleString(),
                  initialLMD: num1Val,
                },
              ],
            });
            return;
          }
        } catch (e) {
          //console.error("解析缓存失败:", e);
          localStorage.removeItem(`pathCache_${cacheKey}`); // 移除损坏的缓存
        }
      }

      //console.log("缓存未命中或无效，开始调用 Transmission");
      const startTime = Date.now();

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("计算超时,请重试")), 15000)
      );

      try {
        const paths = await Promise.race([
          Transmission(difference, filteredItems, {
            upgrade0Limit,
            upgrade1Limit,
            upgrade2Limit,
            sanityLimit,
          },
          num2Val
        ),
          timeoutPromise,
        ]);
        //console.log("Transmission 返回的 paths:", paths ? paths.length : 0, "条");

        if (!paths || paths.length === 0) {
          //console.error("后端返回空路径数组");
          dispatch({
            type: "SET_ERROR",
            field: "differenceError",
            value: "计算完成，但未找到满足条件的路径方案。",
          });
          //paths = [];
          dispatch({ type: "SET_PATHS", paths: [] }); // 确保路径为空
        } else {
          localStorage.setItem(`pathCache_${cacheKey}`, JSON.stringify(paths));
          console.log("缓存已保存，耗时:", Date.now() - startTime, "ms");
          managePathCache(cacheKey);
        }
        dispatch({ type: "SET_PATHS", paths });
        dispatch({
          type: "SET_HISTORY",
          history: [
            ...state.history.slice(-10),
            {
              path: paths[0], // 只记录第一个路径到历史
              timestamp: new Date().toLocaleString(),
              initialLMD: num1Val,
            },
          ],
        });
      } catch (error) {
        //console.error("计算或API调用失败:", error);
        let errorMessage = "发生未知错误，请稍后再试。";

        if (error.isNetworkError) {
          errorMessage = error.message;
        } else if (error.status) {
          switch (error.status) {
            case 400:
              errorMessage = `输入错误: ${error.message}`;
              break;
            case 429:
              errorMessage = `请求过于频繁: ${error.message}`;
              break;
            case 504:
              errorMessage = `计算超时: ${error.message}`;
              break;
            case 500:
              errorMessage = `服务器内部错误: ${error.message}. 如果问题持续，请联系管理员。`;
              break;
            default:
              errorMessage = `请求失败: ${error.message} (代码: ${error.status})`;
          }
        } else if (error.message) {
          errorMessage = `发生错误: ${error.message}`;
        }

        dispatch({
          type: "SET_ERROR",
          field: "differenceError",
          value: errorMessage,
        });
        //paths = [];
        dispatch({ type: "SET_PATHS", paths: [] });
        dispatch({
          type: "SET_HISTORY",
          history: [...state.history.slice(-10), `计算失败: ${errorMessage}`],
        });
      } finally {
        dispatch({ type: "SET_CALCULATING", value: false });
      }
    },
    [
      state.num1,
      state.num2,
      state.settings,
      state.history,
      state.upgrade0Count,
      state.upgrade1Count,
      state.upgrade2Count,
      state.sanityCount,
    ]
  );

  // 管理路径缓存（最多5条）
  const managePathCache = (newKey) => {
    const cacheQueue = JSON.parse(
      localStorage.getItem("pathCacheQueue") || "[]"
    );
    if (!cacheQueue.includes(newKey)) {
      cacheQueue.push(newKey);
      if (cacheQueue.length > 5) {
        const removedKey = cacheQueue.shift();
        localStorage.removeItem(`pathCache_${removedKey}`);
      }
      localStorage.setItem("pathCacheQueue", JSON.stringify(cacheQueue));
    }
  };

  const handleChangePath = useCallback(
    (delta) => {
      if (state.pathCache.length > 0) {
        // [修改] 彩蛋触发逻辑
        if (isBonusReady) {
          // 如果彩蛋已准备好，这次点击就直接显示弹窗
          setShowBonusModal(true);
        } else {
          // 否则，正常推进点击计数
          const newClickCount = state.clickCount + 1;
          if (newClickCount === 29) {
            setIsBonusReady(true);
          }
        }
        dispatch({ type: "CHANGE_PATH", delta });
      }
    },
    // [修改] 依赖项中加入 isBonusReady
    [state.pathCache.length, state.clickCount, isBonusReady]
  );

  // [新增] 创建爱心特效的函数
  const createHeartEffect = (event) => {
    // 获取点击位置
    const x = event.clientX;
    const y = event.clientY;

    // 创建爱心元素
    const heart = document.createElement("div");
    heart.className = "love-heart";
    heart.innerHTML = "❤️";

    heart.style.left = `${x}px`;
    heart.style.top = `${y}px`;

    // 添加到 body
    document.body.appendChild(heart);

    // [关键] 动画结束后自动移除元素
    heart.addEventListener("animationend", () => {
      heart.remove();
    });
  };

  // 新增返回顶部函数
  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // 公共侧边栏组件
  const Sidebar = () => (
    <div className="sidebar">
      <div className="sidebar-title">凑数计算器</div>
      {[
        { to: "/", text: "计算主页" },
        { to: "/note", text: "注意事项" },
        { to: "/data", text: "数据部分" },
        { to: "/about", text: "关于" },
      ].map(({ to, text }) => (
        <div className="sidebar-box" key={to}>
          <Link to={to}>{text}</Link>
        </div>
      ))}
    </div>
  );

  const settingsOptions = [
    { text: "不允许使用理智三星通关", key: "disable3Star" },
    { text: "不允许使用理智二星通关", key: "disable2Star" },
    { text: "不存在/不使用代理剿灭25理智获取250龙门币", key: "disableExt25" },
    { text: "不存在/不使用龙门币副本(CE系列关卡)", key: "disableCE" },
    { text: "不允许使用基建物品合成", key: "disableMaterial" },
    { text: "不允许使用贸易站售卖赤金", key: "disableTrade" },
    { text: "不存在/不使用活动商店1代币换10龙门币", key: "disableStore10" },
    { text: "不存在/不使用活动商店1代币换20龙门币", key: "disableStore20" },
    { text: "不存在/不使用危机合约1代币换70龙门币", key: "disableStore70" },
    { text: "不存在/不使用活动商店5代币换2000龙门币", key: "disableStore2000" },
    { text: "不存在/不使用活动商店7代币换5000龙门币", key: "disableStore5000" },
    { text: "允许连续多次对精零1级干员进行升级", key: "enableUpgradeOnly0" },
    { text: "允许连续多次对精一1级干员进行升级", key: "enableUpgradeOnly1" },
    { text: "允许连续多次对精二1级干员进行升级", key: "enableUpgradeOnly2" },
    {
      text: "只允许连续多次对精零/精一/精二1级干员进行升级",
      key: "enableUpgradeOnlyFor1",
    },
  ];

  return (
    <div className="app-container">
      <Sidebar />

      <button className="back-to-top" onClick={scrollToTop}>
        ↑ 返回顶部
      </button>

      <div className="input-area">
        <div className="main-container">
          <div className="main-content-container">
            <div className="content-panel left-panel">
              <div className="title-bar">
                <h1>龙门币凑数计算器</h1>
              </div>

              <div className="left-panel-title-container">
                <div className="title-text">
                  请输入两个[0,99999999]区间的整数
                </div>
                <div className="title-text">且两数差值处于[-5000,5000]区间</div>
              </div>

              <div className="main-content">
                <div className="input-container">
                  <div className="input-group-horizontal">
                    <div className="input-group">
                      <div className="input-wrapper-text">当前龙门币数量:</div>
                      <div className="input-wrapper">
                        <input
                          type="text"
                          className="input-box"
                          placeholder="请输入数字"
                          value={state.num1}
                          onChange={(e) => handleInputChange(e, "num1")}
                        />
                        {state.error1 && (
                          <div className="error-message">{state.error1}</div>
                        )}
                      </div>
                    </div>

                    <div className="input-group">
                      <div className="input-wrapper-text">目标龙门币数量:</div>
                      <div className="input-wrapper">
                        <input
                          type="text"
                          className="input-box"
                          placeholder="请输入数字"
                          value={state.num2}
                          onChange={(e) => handleInputChange(e, "num2")}
                        />
                        {state.error2 && (
                          <div className="error-message">{state.error2}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="upgrade-count-container">
                  <div className="toggle-container">
                    <div className="toggle-text">允许升级的精零干员数量：</div>
                    <input
                      type="number"
                      className="short-input-box"
                      min="0"
                      max="10"
                      step="1"
                      placeholder="0~10"
                      value={state.upgrade0Count}
                      onChange={(e) =>
                        handleUpgradeCountChange(e, "upgrade0Count", 0, 10)
                      }
                    />
                  </div>
                  <div className="toggle-container">
                    <div className="toggle-text">允许升级的精一干员数量：</div>
                    <input
                      type="number"
                      className="short-input-box"
                      min="0"
                      max="10"
                      step="1"
                      placeholder="0~10"
                      value={state.upgrade1Count}
                      onChange={(e) =>
                        handleUpgradeCountChange(e, "upgrade1Count", 0, 10)
                      }
                    />
                  </div>
                  <div className="toggle-container">
                    <div className="toggle-text">允许升级的精二干员数量：</div>
                    <input
                      type="number"
                      className="short-input-box"
                      min="0"
                      max="10"
                      step="1"
                      placeholder="0~10"
                      value={state.upgrade2Count}
                      onChange={(e) =>
                        handleUpgradeCountChange(e, "upgrade2Count", 0, 10)
                      }
                    />
                  </div>
                  <div className="toggle-container">
                    <div className="toggle-text">允许升级的理智的数量：</div>
                    <input
                      type="number"
                      className="short-input-box"
                      min="0"
                      max="200"
                      step="1"
                      placeholder="0~200"
                      value={state.sanityCount}
                      onChange={(e) =>
                        handleUpgradeCountChange(e, "sanityCount", 0, 200)
                      }
                    />
                  </div>
                </div>

                <button
                  className="calculate-button"
                  onClick={handleCalculate}
                  disabled={state.isCalculating}
                >
                  {state.isCalculating ? "计算中..." : "立即计算"}
                </button>

                <div className="result-section">
                  <div className="output-wrapper-text">
                    计算得到还需要龙门币数量:
                  </div>
                  <div className="result-container">
                    <input
                      type="text"
                      className="result-box"
                      placeholder="两者相差"
                      value={state.result}
                      readOnly
                    />
                  </div>
                  {state.differenceError && (
                    <div className="error-message">{state.differenceError}</div>
                  )}
                </div>

                <div className="usage-guide">
                  <div className="notice-title">注意事项</div>
                  <div className="notice-content">
                    1.点击“立即计算”按钮开始计算，点击页面底部参考路径方案中的“上一路径”和“下一路径”按钮可以切换路径方案。
                    <br />
                    2.设置面板中的开关调整之后，需要重新点击“立即计算”按钮才会生效。
                    如果点击“立即计算”之后不起作用，建议重新点击或者刷新一下网页。
                    对于某些较大的数字可能存在计算较慢的现象，但一般5秒左右能计算出结果。后续会继续优化计算速度。
                  </div>
                </div>
              </div>
            </div>

            <div className="content-panel right-panel">
              <div className="title-bar">
                <h1>设置区域</h1>
              </div>

              <div className="toggle-wrapper">
                {/* --- 第一组：使用理智 --- */}
                <h4 className="settings-group-title">使用理智</h4>
                {settingsOptions.slice(0, 4).map(({ text, key }) => (
                  <div className="toggle-container" key={key}>
                    <div className="toggle-text">{text}</div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={state.settings[key]}
                        onChange={() => handleToggleChange(key)}
                      />
                      <span className="slider" />
                    </label>
                  </div>
                ))}

                {/* --- 第二组：基建 --- */}
                <h4 className="settings-group-title">基建</h4>
                {settingsOptions.slice(4, 6).map(({ text, key }) => (
                  <div className="toggle-container" key={key}>
                    <div className="toggle-text">{text}</div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={state.settings[key]}
                        onChange={() => handleToggleChange(key)}
                      />
                      <span className="slider" />
                    </label>
                  </div>
                ))}

                {/* --- 第三组：使用代币 --- */}
                <h4 className="settings-group-title">使用代币</h4>
                {settingsOptions.slice(6, 11).map(({ text, key }) => (
                  <div className="toggle-container" key={key}>
                    <div className="toggle-text">{text}</div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={state.settings[key]}
                        onChange={() => handleToggleChange(key)}
                      />
                      <span className="slider" />
                    </label>
                  </div>
                ))}

                {/* --- 第四组：干员升级 --- */}
                <h4 className="settings-group-title">干员升级</h4>
                {settingsOptions.slice(11).map(({ text, key }) => (
                  <div className="toggle-container" key={key}>
                    <div className="toggle-text">{text}</div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={state.settings[key]}
                        onChange={() => handleToggleChange(key)}
                      />
                      <span className="slider" />
                    </label>
                  </div>
                ))}

                {/* --- 在最后一组下方，新增提示文字 --- */}
                <p className="settings-footer-note">
                  请注意当“只允许...”按钮开启时，请确保其他三个升级开关中至少有一个为开启状态。
                </p>
              </div>
            </div>
          </div>

          <div className="history-box">
            
            {state.isCalculating ? (
              <div className="loading-container">
                <div className="progress-bar">
                  <div className="progress-bar-fill"></div>
                </div>
                <p>正在计算路径，请稍候...</p>
              </div>
            ) : state.pathCache.length > 0 ? (
              <PathRenderer
                path={state.pathCache[state.currentPathIndex] || []}
                initialLMD={parseInt(state.num1) || 0}
                totalPaths={state.pathCache.length}
                currentIndex={state.currentPathIndex}
                onPrevPath={() => handleChangePath(-1)}
                onNextPath={() => handleChangePath(1)}
                // [新增] 将彩蛋准备状态传递给 PathRenderer 组件
                isBonusReady={isBonusReady}
                activeImageUrl={activeImageUrl}
              />
            ) : (
              <div className="no-path">{""}</div>
            )}

            {state.pathCache.length > 0 &&
              state.clickCount >= 10 &&
              state.clickCount < 30 && (
                <div className="change-over-text">
                  <p>
                    {state.clickCount < 20
                      ? "已经尝试过所有路径"
                      : "再按几次，好像有什么东西要出来了？"}
                  </p>
                </div>
              )}
          </div>
        </div>
      </div>

      <div className="footer">
        <a target="_blank" rel="noreferrer noopener" className="external-link2">
          鄂ICP备2025105560号-1
        </a>
        <a className="external-link2">© 2025 龙门币凑数计算器（https://ark-lmd.top）</a>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>提醒</h3>
            <p>
              您已开启“只允许连续多次对精零/精一/精二1级干员进行升级”开关，请检查以下开关至少有一个是打开的，否则无法计算出结果。当前开关状态：
            </p>
            <p>
              <p>
                允许连续多次对精零1级干员进行升级：
                {state.settings.enableUpgradeOnly0 ? "已开启" : "未开启"}
              </p>
              <p>
                允许连续多次对精一1级干员进行升级：
                {state.settings.enableUpgradeOnly1 ? "已开启" : "未开启"}
              </p>
              <p>
                允许连续多次对精二1级干员进行升级：
                {state.settings.enableUpgradeOnly2 ? "已开启" : "未开启"}
              </p>
            </p>
            <button onClick={() => setShowModal(false)}>关闭</button>
          </div>
        </div>
      )}

      {/* [修改] 为彩蛋弹窗的根元素动态添加 'show' 类，用于控制CSS动画 */}
      <div className={`modal-overlay ${showBonusModal ? "show" : ""}`}>
        {/* Bonus Modal 内容不变，但现在它的显示/隐藏会受CSS控制 */}
        {showBonusModal && (
          <div className="modal-content bonus-modal">
            <img
              src="https://ark-lmd.oss-cn-beijing.aliyuncs.com/rosmontis5.webp"
              alt="Bonus"
              className="bonus-image"
            />
            <p className="bonus-text">
              迷迭香发现你点了好多次按钮，她提醒你记得休息一下
            </p>
            {/* [修改] 关闭弹窗时，同时重置彩蛋准备状态，以便下次还能触发 */}
            <button
              onClick={() => {
                setShowBonusModal(false);
                setIsBonusReady(false); // 重置状态
              }}
            >
              关闭
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const App = () => (
  <Router>
    <Routes>
      <Route path="/" element={<MainCalculator />} />
      <Route path="/note" element={<NotePage />} />
      <Route path="/data" element={<DataPage />} />
      <Route path="/about" element={<AboutPage />} />
    </Routes>
  </Router>
);

export default App;